import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type { BufferedChangeset } from 'ember-changeset/types'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { hash, concat, uniqueId } from '@ember/helper'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import { FormFieldLabel } from 'teacher-ui-client/components/form-field/label'
import { FormFieldInput } from 'teacher-ui-client/components/form-field/input'
import FormSelect from 'teacher-ui-client/components/form-components/form-select'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { isSelectOption, type SelectOption } from 'teacher-ui-client/components/form-components/form-select/option'

interface Args {
  changeset: BufferedChangeset
  labelText: string
  propertyName: string
}

interface FieldItem {
  label: any
  input: any
  select: any
  labelText: string
  fieldId: string
  propertyName: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
  Blocks: {
    default: [FieldItem]
  }
}

/**
 * The formfield component creates a field which can be used to compose flexible form elements
 * which have access to error handling and accessibility features in providing shared state.
 * The example below illustrates how the component can be used for a normal textfield, where a fieldId
 * is used for the form label and input components without us specifically specifying the id.
 *
 * @example
 * <FormField
 *   @changeset={{this.changeset}}
 *   @propertyName='firstName'
 *   @labelText={{t "manage.firstName"}}
 *   as |field|
 * >
 *  <field.label />
 *  <field.input />
 * </FormField>
 */
export class FormField extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  /**
   * Touched state of the form field. Has it been changed/interacted with?
   * Used to indicate whether errors are displayed for a field
   */
  @tracked isTouched = false

  private get propertyError() {
    return this.args.changeset.get(`error.${this.args.propertyName}`)
  }

  /**
   * Indicates when validation errors should show.
   * Don't show them before this input has been interacted with by the user.
   */
  get shouldShowError(): boolean {
    return this.propertyError && this.isTouched
  }

  /**
   * Massages the validation. ember-changeset returns a string for a single validation, else an array for multiple
   * error validations. This getter always returns it as an array
   */
  private get propertyValidations() {
    return this.propertyError?.validation ?? []
  }

  // Most of the time we only need to display a single validation error
  get filteredValidations() {
    return this.propertyValidations.slice(0, 1)
  }

  @action
  onInput(event: Event & { target: HTMLInputElement }) {
    void this.updateChangeset(event.target.value)
  }

  @action
  onInputChecked(event: Event & { target: HTMLInputElement }) {
    void this.updateChangeset(event.target.checked)
  }

  @action
  onOptionClick(value: any) {
    void this.updateChangeset(value)
  }

  @action
  onOptionReset() {
    this.args.changeset.rollbackProperty(this.args.propertyName)
  }

  /**
   * Validate only this property on focus out
   */
  @action
  async onFocusOut() {
    await this.args.changeset.validate(this.args.propertyName)
    this.isTouched = true
  }

  /**
   * Sets the fields property to the passed in value, then validate all fields
   */
  @action
  private async updateChangeset(value: any) {
    this.args.changeset.set(this.args.propertyName, value)
    await this.args.changeset.validate()
    this.isTouched = true
  }

  get valueForInput(): string | undefined {
    const value = this.args.changeset.get(this.args.propertyName) as unknown
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
  }

  get valueForSelect(): string | number | SelectOption | undefined {
    const value = this.args.changeset.get(this.args.propertyName) as unknown
    if (typeof value === 'string' || typeof value === 'number' || isSelectOption(value)) return value
  }

  <template>
    {{#let (concat (uniqueId) "-" @propertyName) as |fieldId|}}
      <div data-test-field={{@propertyName}} class="relative" ...attributes>
        {{yield
          (hash
            label=(component FormFieldLabel for=fieldId text=@labelText)
            input=(component
              FormFieldInput
              id=fieldId
              value=this.valueForInput
              name=@propertyName
              onInput=this.onInput
              onFocusOut=this.onFocusOut
              outlineForced=this.shouldShowError
              themeKey=(if this.shouldShowError ThemeKeys.orange (themeKey this.subscriptionType.current))
            )
            select=(component
              FormSelect
              class=(if this.shouldShowError "outline-solid outline-2 outline-juicy-orange-300/75")
              value=this.valueForSelect
              id=fieldId
              optionClick=this.onOptionClick
              resetAction=this.onOptionReset
              closeAction=this.onFocusOut
            )
            labelText=@labelText
            fieldId=fieldId
            propertyName=@propertyName
          )
        }}
        {{#if this.shouldShowError}}
          <ul
            data-test-error-list
            class="z-tooltip bg-juicy-orange-300 after:border-b-juicy-orange-300 absolute mt-2 rounded-sm px-2 py-1 shadow-md after:absolute after:-top-1 after:border-x-4 after:border-b-4 after:border-x-transparent"
          >
            {{#each this.filteredValidations as |message|}}
              <li class="text-sm text-white">{{message}}</li>
            {{/each}}
          </ul>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

export default FormField
